<script setup lang="ts">
import { computed, ref } from 'vue';
import { APPLICATION_TYPES } from '@hfc/common/src/types/ApplicationTypes';
import { SmallBusinessEmployee } from '@/application/smallBusiness/domain/SmallBusinessEmployee';
import { SmallBusinessMember } from '@/application/smallBusiness/domain/SmallBusinessMember';
import DobTypeSelector from '@/application/smallBusiness/infrastructure/components/DobTypeSelector.vue';
import { Relationships, type Relationship } from '@/application/smallBusiness/domain/SmallBusinessRelationships';
import { Field } from 'vee-validate';
import { UUID } from '@hfc/common/src/types/UUID';
import { dateHelpers, LocationSelector } from '@hfc/ui-core';
import SmallBusinessAddPersonButton from '@/application/smallBusiness/infrastructure/components/SmallBusinessAddPersonButton.vue';
import { useCheckCoverage } from '@/quote/infrastructure/composition/useCheckCoverage';

const { checkCoverage } = useCheckCoverage();
const locationSelectorRef = ref<InstanceType<typeof LocationSelector> | null>(null);

const props = withDefaults(defineProps<{
  person: SmallBusinessEmployee | SmallBusinessMember;
  index: number;
  label: string;
  showRelationship?: boolean;
  showAddDependentsButton?: boolean;
  validateCoverage?: boolean;
  compact?: boolean;
  showDependentsType?: boolean;
  showDependentsCount?: boolean;
  showIndex?: boolean;
  containerClass?: string;
  canRemove?: boolean;
  autoFocusDate?: boolean;
}>(), {
  canRemove: true,
});

interface Emits {
  (e: 'updatePerson', value: unknown, field: string, id: UUID): void;
  (e: 'addDependents'): void;
  (e: 'remove'): void;
}

const emit = defineEmits<Emits>();

const { getDateFromAge } = dateHelpers();

const firstName = ref(props.person.first_name);
const lastName = ref(props.person.last_name);
const localDateOfBirth = computed(() => props.person.date_of_birth);

const spouseKey = Relationships.Spouse.key as Relationship;
const childKey = Relationships.Child.key as Relationship;
const smallBusinessEmployeeKey = props.person as SmallBusinessEmployee;

const personMinAge = computed(() => props.person.relationship === Relationships.Spouse.key || props.person.relationship === Relationships.Self.key ? 18 : 0);
const personMaxAge = computed(() => props.person.relationship === Relationships.Child.key ? 26 : 120);

const showDependentsButton = computed(() => {
  if ('dependents' in props.person && props.person.dependents) {
    return props.person.dependents.length < 1;
  }

  return false;
});

const updatePerson = (value: unknown, field: string, id: UUID): void => {
  emit('updatePerson', value, field, id);
};

const removePerson = (): void => {
  emit('remove');
};

const getDependentType = (relationship: Relationship): string => {
  const rel = Object.values(Relationships).find(r => r.key === relationship);

  return rel ? rel.label : '';
};

defineExpose({
  isValid: (): boolean => props.validateCoverage ? !!locationSelectorRef.value?.isValidLocation() : true
});
</script>
<template>
  <div class="relative py-4" :class="containerClass">
    <div class="flex justify-between">
      <div v-if="showDependentsType" class="font-semibold text-blue-500">
        {{ getDependentType(person.relationship) }} {{ showIndex ? index + 1 : '' }}
      </div>
    </div>
    <div class="flex flex-col lg:flex-row space-y-4 lg:space-y-0 lg:space-x-2 2xl:space-x-4">
      <div class="flex-1">
        <label class="font-medium mb-1 whitespace-nowrap text-sky-700 block">
          First Name <span class="font-serif">*</span>
        </label>
        <Field v-model="firstName" type="text" v-slot="{ field, errorMessage, meta }" :name="`employee-${person.id}-name`" rules="required|min:2|max:30">
          <input v-capitalized="field" v-bind="field" :class="{ ...meta }" @change="() => updatePerson(firstName, 'first_name', person.id)">
          <span v-if="errorMessage" class="error">{{ errorMessage }}</span>
        </Field>
      </div>
      <div class="flex-1">
        <label class="font-medium mb-1 whitespace-nowrap text-sky-700 block">
          Last Name <span class="font-serif">*</span>
        </label>
        <Field v-model="lastName" type="text" v-slot="{ field, errorMessage, meta }" :name="`employee-${person.id}-last_name`" rules="required|min:2|max:30">
          <input v-capitalized="field" v-bind="field" :class="{ ...meta }" @change="() => updatePerson(lastName, 'last_name', person.id)">
          <span v-if="errorMessage" class="error">{{ errorMessage }}</span>
        </Field>
      </div>
      <div class="flex flex-1 md:flex-none lg:w-36 2xl:w-44 flex-col">
        <Field v-model="localDateOfBirth" v-slot="{ errorMessage, meta }" :name="`person-${props.label}-${index}-date`" :rules="{ required: true, not_one_of: [0], min_date: getDateFromAge(personMinAge), max_date: getDateFromAge(personMaxAge) }">
          <DobTypeSelector :id="`person-${props.label}-${index}-dob`" v-model="localDateOfBirth" date-label="DOB" type="age" :has-errors="!meta.valid && meta.validated" @update:model-value="val => updatePerson(val, 'date_of_birth', person.id)" class="mt-1" :start-age="personMinAge" :auto-focus="autoFocusDate" :use-input-on-age="true" />
          <span v-if="errorMessage" class="error">{{errorMessage}}</span>
        </Field>
      </div>
      <div class="flex-1">
        <LocationSelector
          class="gap-4"
          ref="locationSelectorRef"
          placeholder=""
          :id="`employee-${person.id}-location`"
          :hide-labels="false"
          :zipcode="person.zip_code"
          :county="person.county"
          :validate-coverage="validateCoverage"
          :validate-restricted-zip-codes="true"
          :show-coverage-error="validateCoverage"
          :show-required-on-label="true"
          :application-type="APPLICATION_TYPES.SMALL_BUSINESS"
          :check-coverage="checkCoverage"
          :vertical-mode="false"
          zip-size="w-full"
          county-size="w-full"
          @update:zip-code="updatePerson($event, 'zip_code', person.id)"
          @update:county="updatePerson($event, 'county', person.id)" />
      </div>
      <div class="flex justify-end">
        <div class="flex lg:h-14 lg:mt-7 flex-1 items-center justify-end gap-2">
          <div v-if="showDependentsButton" class="flex space-x-1 2xl:space-x-4">
            <SmallBusinessAddPersonButton :person="smallBusinessEmployeeKey" :person-relationship="spouseKey" />
            <SmallBusinessAddPersonButton :person="smallBusinessEmployeeKey" :person-relationship="childKey" />
          </div>
          <button v-if="canRemove" tabindex="-1" type="button" @click.prevent="removePerson" class="text-red-300 text-2xl font-bold transform duration-300 hover:text-red-500">
            <hfc-icon icon="times" class="px-2" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
